import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";

import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import PatientsPage from "./pages/PatientsPage";
import SessionsPage from "./pages/SessionsPage";
import RelationGraphPage from "./pages/RelationGraphPage";
import BrainMapPage from "./pages/BrainMapPage";
import ReviewsPage from "./pages/ReviewsPage";
import SettingsPage from "./pages/SettingsPage";

import AdminApprovalsPage from "./pages/AdminApprovalsPage";
import AdminSystemPage from "./pages/AdminSystemPage";

import ResearchPage from "./pages/ResearchPage";

import AppLayout from "./components/AppLayout";

const THEME_STORAGE_KEY = "ui_theme";

function AdminOnly({ user, children }) {
  if (user?.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

export default function App() {
  const [user, setUser] = useState(null);
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(
    () => localStorage.getItem(THEME_STORAGE_KEY) || "light"
  );

  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");
    const name = localStorage.getItem("user_name");
    const userId = localStorage.getItem("user_id");

    if (token && role) {
      setUser({
        access_token: token,
        role,
        name: name || "",
        user_id: userId || "",
      });
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("user_name");
    localStorage.removeItem("user_id");
    setUser(null);
    setMode("login");
  };

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  if (loading) {
    return (
      <div className="app-shell">
        <div className="loading-screen">
          <div className="loading-card">Loading workspace...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="app-shell">
        {mode === "login" ? (
          <Login
            onLogin={setUser}
            onSwitch={() => setMode("register")}
            theme={theme}
          />
        ) : (
          <Register
            onSwitch={() => setMode("login")}
            theme={theme}
          />
        )}
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="app-shell">
        <AppLayout
          user={user}
          onLogout={logout}
          theme={theme}
          onToggleTheme={toggleTheme}
        >
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            <Route
              path="/dashboard"
              element={
                <Dashboard
                  user={user}
                  onLogout={logout}
                  theme={theme}
                  onToggleTheme={toggleTheme}
                />
              }
            />

            <Route
              path="/patients"
              element={<PatientsPage user={user} />}
            />

            <Route
              path="/sessions"
              element={<SessionsPage user={user} />}
            />

            <Route
              path="/relation-graph"
              element={<RelationGraphPage user={user} />}
            />

            <Route
              path="/brain-map"
              element={<BrainMapPage user={user} />}
            />

            <Route
              path="/reviews"
              element={<ReviewsPage user={user} />}
            />

            <Route
              path="/research"
              element={<ResearchPage user={user} />}
            />

            <Route
              path="/admin/approvals"
              element={
                <AdminOnly user={user}>
                  <AdminApprovalsPage user={user} />
                </AdminOnly>
              }
            />

            <Route
              path="/admin/system"
              element={
                <AdminOnly user={user}>
                  <AdminSystemPage user={user} />
                </AdminOnly>
              }
            />

            <Route
              path="/settings"
              element={
                <SettingsPage
                  user={user}
                  theme={theme}
                  onToggleTheme={toggleTheme}
                  onLogout={logout}
                />
              }
            />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </AppLayout>
      </div>
    </BrowserRouter>
  );
}